// src/pages/CalendarPage/CalendarPage.tsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, RotateCw, Lightbulb, X } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { useRetinolStore } from '../../store/retinolStore';
import { useTelegramWebApp } from '../../hooks/useTelegramWebApp';
import ApplicationModal from './components/ApplicationModal';
import { ApiStatusIndicator } from './components/ApiStatusIndicator';
import { StageUpgradeModal } from '../../components/ui/StageUpgradeModal';
import { Button } from '../../components/ui/Button';
import {
  MAX_PAST_DAYS_TO_MARK,
  MESSAGES,
  CONTAINER_PADDING,
  CONTAINER_PADDING_SMALL,
  SPACING,
} from '../../utils/constants';
import { logger } from '../../utils/logger';
import { POSITIVE_MESSAGES, MOTIVATIONAL_MESSAGES, FACT_CATEGORY_LABELS, type RetinolFact } from '../../data/messages';
import { getFactOfTheDay, getRandomDifferentFact } from '../../utils/factsManager';
import { useAuthStore } from '../../store/authStore';

// ✅ Новый импорт витрины продуктов
import { ProductShowcase } from '../../components/products/ProductShowcase';

/* ===================== STYLED ===================== */
const PageContainer = styled.div`
  width: 100%;
  max-width: 430px;
  padding: ${CONTAINER_PADDING.top}px ${CONTAINER_PADDING.horizontal}px ${CONTAINER_PADDING.bottom}px;
  margin: 0 auto;
  box-sizing: border-box;
  /* ✅ КРИТИЧНО для iOS: используем динамический viewport вместо 100vh */
  min-height: var(--tg-viewport-stable-height, 100vh);
  background: var(--bg-secondary);
  overflow-x: hidden;

  [data-theme="dark"] & {
    background:
      radial-gradient(circle at 20% 20%, rgba(157, 142, 238, 0.12) 0%, transparent 55%),
      radial-gradient(circle at 80% 80%, rgba(160, 216, 240, 0.12) 0%, transparent 55%),
      var(--bg-primary);
  }

  @media (max-width: 375px) {
    padding: ${CONTAINER_PADDING_SMALL.top}px ${CONTAINER_PADDING_SMALL.horizontal}px ${CONTAINER_PADDING_SMALL.bottom}px;
  }
`;

const Header = styled(motion.header)`
  text-align: center;
  margin-bottom: 28px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;
  background: var(--gradient-logo);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: var(--text-secondary);
  margin: 8px 0 0;
  line-height: 1.5;
`;

const FactCard = styled(motion.section)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border-radius: 24px;
  background: var(--surface-medium);
  border: 1px solid var(--border-medium);
  box-shadow: 0 16px 40px rgba(123, 104, 238, 0.18);
  margin-bottom: 28px;
  box-sizing: border-box;

  [data-theme="dark"] & {
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
  }

  /* ✅ FIX: Адаптация padding для узких экранов */
  @media (max-width: 375px) {
    padding: 16px;
    gap: 14px;
    border-radius: 20px;
  }
`;

const FactHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const FactIcon = styled.div`
  flex-shrink: 0;
  color: #7b68ee;
  filter: drop-shadow(0 6px 12px rgba(153, 102, 204, 0.25));
  display: flex;
  align-items: center;
  justify-content: center;

  [data-theme="dark"] & {
    color: #C4B5FD;
    filter: drop-shadow(0 6px 12px rgba(196, 181, 253, 0.25));
  }
`;

const FactContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const FactTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FactTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const FactBadge = styled.span<{ $category: 'practice' | 'science' | 'support' }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.$category) {
      case 'science': return 'rgba(59, 130, 246, 0.15)';
      case 'support': return 'rgba(236, 72, 153, 0.15)';
      default: return 'rgba(34, 197, 94, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.$category) {
      case 'science': return '#2563eb !important';
      case 'support': return '#db2777 !important';
      default: return '#16a34a !important';
    }
  }};
`;

const FactText = styled.p`
  font-size: 15px;
  margin: 0;
  line-height: 1.6;
  color: var(--text-primary);
`;

const FactFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RefreshButton = styled(motion.button)`
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid var(--border-medium);
  background: transparent;
  color: #7b68ee;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--surface-light);
    border-color: var(--border-strong);
  }

  &:active {
    transform: scale(0.97);
  }

  svg {
    flex-shrink: 0;
    color: currentColor;
  }

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const OnboardingHint = styled(motion.div)`
  width: 100%;
  margin: 0 0 ${SPACING.card}px 0;
  padding: 16px;
  background: var(--surface-medium);
  border-radius: 20px;
  border: 2px solid var(--border-medium);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;

  /* ✅ FIX: Адаптация padding для узких экранов */
  @media (max-width: 375px) {
    padding: 12px;
    border-radius: 16px;
  }
`;

const HintHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HintTitle = styled.h4`
  font-size: 17px;
  font-weight: 700;
  color: #7B68EE;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const HintIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7B68EE;

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const HintBody = styled(motion.div)`
  margin-top: 12px;
`;

const HintText = styled.p`
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0 0 8px 0;
  line-height: 1.6;
`;

const CalendarSection = styled(motion.section)`
  width: 100%;
  padding: 24px 18px;
  border-radius: 24px;
  background: var(--card-bg);
  border: 1px solid var(--border-medium);
  box-shadow: 0 16px 40px rgba(123, 104, 238, 0.15);
  overflow: hidden;
  box-sizing: border-box;

  [data-theme="dark"] & {
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.3);
  }

  /* ✅ FIX: Адаптация padding для узких экранов */
  @media (max-width: 375px) {
    padding: 16px 8px;
    border-radius: 20px;
  }
`;

const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const MonthButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 14px;
  border: none;
  background: var(--surface-light);
  color: #7b68ee;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px) scale(1.05);
    background: var(--surface-medium);
  }

  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const MonthTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-transform: capitalize;
`;

const WeekdaysGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  box-sizing: border-box;

  /* ✅ FIX: Уменьшаем gap для узких экранов */
  @media (max-width: 375px) {
    gap: 4px;
  }
`;

const Weekday = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  color: var(--text-tertiary);
`;

// ✅ Task 1: Добавлен статус 'applied' для дней с application вне плана
type DayStatus = 'completed' | 'missed' | 'today' | 'upcoming' | 'inactive' | 'applied';

const DaysGrid = styled(motion.div)`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
  box-sizing: border-box;

  /* ✅ FIX: Уменьшаем gap для узких экранов */
  @media (max-width: 375px) {
    gap: 4px;
  }
`;

const DayButton = styled(motion.button)<{ $status: DayStatus; $isToday?: boolean }>`
  aspect-ratio: 1;
  width: 100%;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;
  box-sizing: border-box;

  /* Фикс перекрытия Telegram-стилей кнопок на мобильных: фиксируем цвета через кастомные переменные */
  --day-bg: ${props => {
    switch (props.$status) {
      case 'completed': return '#34d399';
      case 'missed': return '#d1d5db';
      case 'today': return 'transparent';
      case 'upcoming': return '#c4b5fd';
      case 'applied': return '#60a5fa';
      default: return '#f3f4f6';
    }
  }};

  --day-color: ${props => {
    switch (props.$status) {
      case 'inactive': return 'rgba(27, 19, 63, 0.45)';
      case 'missed': return '#4b5563';
      case 'today': return '#7b68ee';
      case 'upcoming': return '#5b21b6';
      case 'applied': return '#ffffff';
      default: return '#ffffff';
    }
  }};

  --day-shadow: ${props => {
    switch (props.$status) {
      case 'completed': return '0 4px 12px rgba(52, 211, 153, 0.25)';
      case 'missed': return '0 2px 8px rgba(148, 163, 184, 0.2)';
      case 'today': return 'none';
      case 'upcoming': return '0 4px 12px rgba(123, 104, 238, 0.2)';
      case 'applied': return '0 4px 12px rgba(96, 165, 250, 0.25)'; 
      default: return 'none';
    }
  }};

  /* ✅ БАГ #1 FIX: Обводка для сегодня (в т.ч. today+planned) */
  border: ${props => (props.$status === 'today' || props.$isToday) ? '3px solid #7b68ee' : 'none'};

  background: var(--day-bg);

  /* ✅ FIX: Темнее фон для незапланированных ячеек на темной теме */
  [data-theme="dark"] &:is(:disabled) {
    background: ${props => props.$status === 'inactive' ? 'rgba(30, 30, 45, 0.6)' : undefined};
  }

  color: var(--day-color);

  /* ✅ FIX: Цвет текста и обводки "Сегодня" в темной теме */
  [data-theme="dark"] & {
    color: ${props => props.$status === 'today' ? '#C4B5FD' : undefined};
    border-color: ${props => (props.$status === 'today' || props.$isToday) ? '#C4B5FD' : undefined};
  }

  box-shadow: var(--day-shadow);

  /* Повышаем специфичность, чтобы статусные цвета не перекрывались мобильными темами */
  && {
    background: var(--day-bg);
    color: var(--day-color);
    box-shadow: var(--day-shadow);
  }

  &:disabled {
    cursor: default;
    opacity: ${props => (props.$status === 'inactive' ? 0.6 : 1)};
  }

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  /* ✅ Outline для темной темы - улучшает видимость границ клеток */
  [data-theme="dark"] & {
    outline: 1px solid rgba(255, 255, 255, 0.08);
    outline-offset: -1px;
  }

  /* ✅ FIX: Уменьшаем font-size для узких экранов */
  @media (max-width: 375px) {
    font-size: 14px;
    border-radius: 14px;
  }
`;

const Legend = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 24px;
  box-sizing: border-box;

  /* ✅ FIX: Уменьшаем minmax для узких экранов */
  @media (max-width: 375px) {
    grid-template-columns: repeat(auto-fit, minmax(95px, 1fr));
    gap: 8px;
    margin-top: 20px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const LegendSwatch = styled.span<{ $status: DayStatus }>`
  width: 20px;
  height: 12px;
  border-radius: 999px;
  box-sizing: border-box;

  /* ✅ FIX БАГ #3: "Сегодня" отображается outline как в календаре */
  border: ${props => props.$status === 'today' ? '2px solid #7b68ee' : 'none'};

  background: ${props => {
    switch (props.$status) {
      case 'completed': return '#34d399';
      case 'missed': return '#d1d5db';
      case 'today': return 'transparent'; // ✅ Прозрачный фон для "Сегодня"
      case 'upcoming': return '#c4b5fd';
      case 'applied': return '#60a5fa'; // ✅ Синий для "Ранее применял"
      default: return '#f3f4f6';
    }
  }};

  /* ✅ FIX: Цвет border "Сегодня" в темной теме */
  [data-theme="dark"] & {
    border-color: ${props => props.$status === 'today' ? '#C4B5FD' : undefined};
  }
`;

// Task 3: EmptyState для пустых месяцев
const EmptyStateCard = styled(motion.div)`
  text-align: center;
  padding: 48px 24px;
  color: var(--text-tertiary);

  svg {
    margin: 0 auto 16px;
    color: var(--text-tertiary);
    opacity: 0.5;
  }

  h4 {
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 8px;
    color: var(--text-secondary);
  }

  p {
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
  }
`;

/**
 * ✅ УЛУЧШЕННЫЙ ТОСТ-ПОРТАЛ с безопасным отступом
 *
 * Расчёт безопасного нижнего отступа:
 * - env(safe-area-inset-bottom): отступ от края экрана (для iPhone с вырезом)
 * - 64px: высота нижней навигации
 * - 12px: дополнительный отступ
 *
 * Это гарантирует видимость на всех устройствах, включая:
 * - iPhone 14 Pro Max (430x932 с Dynamic Island)
 * - Планшеты с большим экраном
 * - Устройства с физическими кнопками внизу
 */
const ToastPortal = styled.div`
  position: fixed;
  bottom: calc(64px + 12px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  pointer-events: none;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  z-index: 3000; /* Выше навигации (z-index: 1000) */

  /* ✅ Адаптация под планшеты */
  @media (min-width: 768px) {
    bottom: calc(64px + 24px + env(safe-area-inset-bottom, 0px));
  }
`;

const ToastCard = styled(motion.div)<{ $tone: 'positive' | 'neutral' }>`
  pointer-events: all;
  max-width: 360px;
  width: 100%;
  border-radius: 20px; /* ✅ Увеличен радиус 16→20 */
  padding: 16px 44px 16px 20px; /* ✅ Место для кнопки закрытия справа */
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  position: relative; /* ✅ Для позиционирования кнопки закрытия */

  /* ✅ Переносы слов, максимум 2 строки */
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;

  color: ${props => (props.$tone === 'positive' ? '#0f5132' : '#1b133f')};
  background: ${props =>
    props.$tone === 'positive'
      ? 'linear-gradient(135deg, #bbf7d0, #86efac)'
      : 'linear-gradient(135deg, #fde68a, #fcd34d)'};

  /* ✅ Улучшенные тени (2-3 слоя) */
  box-shadow:
    0 8px 16px rgba(27, 19, 63, 0.12),
    0 2px 4px rgba(27, 19, 63, 0.08),
    0 18px 34px rgba(27, 19, 63, 0.18);

  /* ✅ Тонкий полупрозрачный бордер */
  border: 1px solid ${props =>
    props.$tone === 'positive'
      ? 'rgba(34, 197, 94, 0.3)'
      : 'rgba(234, 179, 8, 0.3)'};

  /* ✅ Анимации выключаются при prefers-reduced-motion */
  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
  }
`;

/**
 * ✅ Кнопка закрытия тоста
 * - Hit-area ≥ 44×44px (WCAG рекомендация)
 * - aria-label для доступности
 * - currentColor для адаптации к фону
 */
const ToastCloseButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: currentColor;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  /* ✅ Видимый focus ring */
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const LoadingOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9000;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top: 4px solid #7B68EE;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: 16px;
  color: white;
  font-size: 16px;
  font-weight: 600;
`;

/* ===================== HELPERS ===================== */
/**
 * ✅ КРИТИЧНО: Получает дату в формате YYYY-MM-DD с учетом user.timezone
 * При смене часового пояса календарь должен показывать правильную дату
 */
const getDateInUserTimezone = (date: Date, timezone: string): string => {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
};

interface CalendarCell {
  date: Date | null;
  iso: string | null;
  status: DayStatus;
  isToday: boolean;
  isPlanned: boolean;
  isPast: boolean;
}

/* ===================== COMPONENT ===================== */
const CalendarPage = () => {
  // ✅ КРИТИЧНО: Получаем user.timezone для правильного расчета "сегодня"
  const { user, isAuthenticated } = useAuthStore((state) => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
  }));
  const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const userTimezone = user?.timezone || deviceTimezone;

  // ✅ КРИТИЧНО: today с учетом user.timezone - обновляется при смене часового пояса
  const today = new Date();
  const todayISO = getDateInUserTimezone(today, userTimezone);

  const[displayedMonth, setDisplayedMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const[selectedDate, setSelectedDate] = useState<string | null>(null);
  const[toast, setToast] = useState<{ message: string; tone: 'positive' | 'neutral' } | null>(null);
  const[showStageModal, setShowStageModal] = useState(false);
  const[upgradedStage, setUpgradedStage] = useState<1 | 2 | 3>(1);
  const[isSyncing, setIsSyncing] = useState(false);
  const[isHintOpen, setIsHintOpen] = useState(false);

  // ✅ Состояние для факта дня
  const[currentFact, setCurrentFact] = useState<RetinolFact>(() => getFactOfTheDay(today));

  const { hapticFeedback, isReady: isTelegramReady } = useTelegramWebApp();
 
  // Достаём из стора только то, что реально используем
  const {
    plannedDates,
    startDate,
    applications,
    currentStage,
    recommendedProducts,
    markApplication,
    updateMissedDays,
    syncFromBackend,
    loadApplicationsFromCloud,
  } = useRetinolStore((state) => ({
    plannedDates: state.plannedDates,
    startDate: state.startDate,
    applications: state.applications,
    currentStage: state.currentStage,
    recommendedProducts: state.recommendedProducts,
    markApplication: state.markApplication,
    updateMissedDays: state.updateMissedDays,
    syncFromBackend: state.syncFromBackend,
    loadApplicationsFromCloud: state.loadApplicationsFromCloud,
  }));

  const useRetinolBefore = useRetinolStore((state) =>
    state.questionnaire?.use_retinol_before ?? state.useRetinolBeforeBackend ?? false
  );

  const showToast = useCallback((message: string, tone: 'positive' | 'neutral') => {
    setToast({ message, tone });
  },[]);


  // Первый sync
  const syncRan = React.useRef(false);
  useEffect(() => {
    if (!isTelegramReady || !isAuthenticated || syncRan.current) return;
    syncRan.current = true;
    
    const sync = async () => {
      setIsSyncing(true);
      try {
        // ✅ ОПТИМИЗАЦИЯ: Запускаем параллельно вместо последовательно
        // Было: await syncFromBackend() + await loadApplicationsFromCloud() = блокировка до 6 сек
        // Стало: Promise.allSettled = максимум 3 сек (timeout)
        const [syncResult, loadResult] = await Promise.allSettled([
          syncFromBackend(),
          loadApplicationsFromCloud()
        ]);

        if (syncResult.status === 'rejected') {
          logger.error('syncFromBackend error:', syncResult.reason);
        }
        if (loadResult.status === 'rejected') {
          logger.error('loadApplicationsFromCloud error:', loadResult.reason);
        }

        // Показываем toast только если обе операции failed
        if (syncResult.status === 'rejected' && loadResult.status === 'rejected') {
          showToast(MESSAGES.calendar.syncError, 'neutral');
        }

      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : MESSAGES.calendar.syncError;
        logger.error('Sync error:', message);
        showToast(MESSAGES.calendar.syncError, 'neutral');
      } finally {
        setIsSyncing(false);
      }
    };

    sync();
  },[isAuthenticated, isTelegramReady, loadApplicationsFromCloud, showToast, syncFromBackend]);

  useEffect(() => {
    if (!isAuthenticated) {
      syncRan.current = false;
    }
  },[isAuthenticated]);
  
  // Модалка апгрейда стадии
  const prevStageRef = React.useRef(currentStage);
  useEffect(() => {
    if (currentStage > prevStageRef.current) {
      setUpgradedStage(currentStage);
      setShowStageModal(true);
    }
    prevStageRef.current = currentStage;
  },[currentStage]);

  // Пересчёт пропусков при изменении отметок/плана
  useEffect(() => {
    updateMissedDays();
  },[applications, plannedDates, updateMissedDays]);

  const planSet = useMemo(() => {
    // Task 3: Убран fallback - показываем только dates[] с бэкенда
    const filtered = plannedDates.filter((dateIso) => {
      const date = new Date(`${dateIso}T00:00:00`);
      return date.getFullYear() === displayedMonth.getFullYear() && date.getMonth() === displayedMonth.getMonth();
    });

    return new Set(filtered);
  },[displayedMonth, plannedDates]);

  const markedSet = useMemo(() => {
    const entries = applications.filter((app) => app.marked).map((app) => app.date);
    return new Set(entries);
  },[applications]);

  const calendarCells = useMemo(() => {
    const monthStart = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
    const monthEnd = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 0);
    const firstDayIndex = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1;
    const cells: CalendarCell[] =[];

    for (let i = 0; i < firstDayIndex; i += 1) {
      cells.push({ date: null, iso: null, status: 'inactive', isToday: false, isPlanned: false, isPast: false });
    }

    for (let day = 1; day <= monthEnd.getDate(); day += 1) {
      const date = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), day);
      // ✅ FIX БАГ #2: Используем formatInTimeZone для корректной работы с user.timezone
      const iso = formatInTimeZone(date, userTimezone, 'yyyy-MM-dd');
      const isPlanned = planSet.has(iso);
      const isMarked = markedSet.has(iso);
      // ✅ КРИТИЧНО: Сравниваем ISO строки напрямую для корректной работы с timezone
      const isToday = todayISO === iso;
      const isPast = iso < todayISO; // Сравнение строк YYYY-MM-DD
      let status: DayStatus = 'inactive';

      // ✅ БАГ #2 FIX: Если сегодня И отмечено → зелёный (приоритет)
      if (isToday && isMarked) {
        status = 'completed';
      }
      // ✅ БАГ #1 FIX: Если сегодня И в плане → заливка + обводка
      else if (isToday && isPlanned) {
        status = 'upcoming';
      }
      // ✅ Сегодня без плана → только обводка
      else if (isToday) {
        status = 'today';
      }
      // ✅ Разделение "Применено" и "Ранее применяли"
      // Если дата в прошлом И отмечена:
      // - ДО startDate → "Ранее применяли" (синий)
      // - ПОСЛЕ startDate → "Применено" (зеленый)
      else if (isPast && isMarked) {
        if (startDate && iso < startDate) {
          status = 'applied'; // Исторический период
        } else {
          status = 'completed'; // Текущий план
        }
      }
      // ✅ Логика статусов для запланированных дней
      else if (isPlanned) {
        if (isPast) {
          // ✅ ПРАВИЛЬНО: Если пользователь УЖЕ применял ретинол,
          // то ВСЕ прошлые запланированные даты = "Ранее применяли" (синие)
          if (useRetinolBefore) {
            status = 'applied';
          } else {
            status = 'missed';
          }
        } else {
          // В будущем → светло-фиолетовый "Запланировано"
          status = 'upcoming';
        }
      }

      cells.push({ date, iso, status, isToday, isPlanned, isPast });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ date: null, iso: null, status: 'inactive', isToday: false, isPlanned: false, isPast: false });
    }

    return cells;
  },[displayedMonth, planSet, markedSet, startDate, todayISO, userTimezone, useRetinolBefore]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timer);
  },[toast]);

  const handleDayClick = useCallback(
    (cell: CalendarCell) => {
      if (!cell.iso || !cell.isPlanned) return;

      if (markedSet.has(cell.iso)) {
        showToast(MESSAGES.validation.alreadyMarked, 'neutral');
        (hapticFeedback as any)('light');
        return;
      }

      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0);

      const targetDate = new Date(cell.iso);
      targetDate.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor((todayDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff < 0) {
        showToast(MESSAGES.validation.futureDay, 'neutral');
        (hapticFeedback as any)('error');
        return;
      }

      if (daysDiff > MAX_PAST_DAYS_TO_MARK) {
        showToast(MESSAGES.validation.pastDays + ' 📅', 'neutral');
        (hapticFeedback as any)('warning');
        return;
      }

      (hapticFeedback as any)('light');
      setSelectedDate(cell.iso);
    },
[hapticFeedback, markedSet, showToast]
  );

  const handleModalAnswer = useCallback(
    async (applied: boolean) => {
      if (!selectedDate) return;

      if (applied) {
        try {
          await Promise.resolve(markApplication(selectedDate, true));

          const s = useRetinolStore.getState();
          s.updateCurrentStreak();
          s.checkStageUpgrade();
          await s.checkAndUnlockAchievements();

          showToast(POSITIVE_MESSAGES[Math.floor(Math.random() * POSITIVE_MESSAGES.length)], 'positive');
          (hapticFeedback as any)('success');
        } catch (error: any) {
          showToast(error?.message || 'Ошибка сохранения', 'neutral');
          (hapticFeedback as any)('error');
        }
      } else {
        showToast(MOTIVATIONAL_MESSAGES[Math.floor(Math.random() * MOTIVATIONAL_MESSAGES.length)], 'neutral');
        (hapticFeedback as any)('warning');
      }

      setSelectedDate(null);
    },
[hapticFeedback, markApplication, selectedDate, showToast]
  );

  const handlePrevMonth = () => {
    setDisplayedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setDisplayedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const monthLabel = useMemo(
    () => displayedMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }),
[displayedMonth]
  );

  // ✅ Обработчик обновления факта
  const handleRefreshFact = useCallback(() => {
    const newFact = getRandomDifferentFact(currentFact.text);
    setCurrentFact(newFact);
    (hapticFeedback as any)('light');
    logger.info('Fact refreshed manually');
  },[currentFact.text, hapticFeedback]);

  // ✅ ВАРИАНТ В: Логика показа тостов перенесена в App.tsx
  // Теперь тосты достижений показываются глобально на всех страницах

  // ===================== RETURN =====================
  return (
    <PageContainer>
      <Header initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Title>Календарь ретинола</Title>
        <Subtitle>Следите за регулярностью и отмечайте результаты ухода.</Subtitle>
      </Header>

      {/* Календарь */}
      <CalendarSection initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
        <MonthHeader>
          <MonthButton onClick={handlePrevMonth} aria-label="Предыдущий месяц">‹</MonthButton>
          <MonthTitle>{monthLabel}</MonthTitle>
          <MonthButton onClick={handleNextMonth} aria-label="Следующий месяц">›</MonthButton>
        </MonthHeader>

        {/* Task 3: EmptyState если нет запланированных дней в месяце */}
        {planSet.size === 0 ? (
          <EmptyStateCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <h4>Нет запланированных дней</h4>
            <p>В этом месяце пока нет дней применения ретинола</p>
          </EmptyStateCard>
        ) : (
          <>
            <WeekdaysGrid>
              {['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'].map((day) => (
                <Weekday key={day}>{day}</Weekday>
              ))}
            </WeekdaysGrid>

            <DaysGrid initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              {calendarCells.map((cell, index) => (
                <DayButton
              key={cell.iso ?? `empty-${index}`}
              $status={cell.status}
              $isToday={cell.isToday}
              disabled={!cell.iso || !cell.isPlanned}
              onClick={() => handleDayClick(cell)}
              whileHover={{ scale: cell.iso && cell.isPlanned ? 1.05 : 1 }}
              whileTap={{ scale: cell.iso && cell.isPlanned ? 0.97 : 1 }}
              layout={false}
            >
              {cell.date ? cell.date.getDate() : ''}
            </DayButton>
          ))}
        </DaysGrid>

        <Legend>
          <LegendItem>
            <LegendSwatch $status="completed" />
            Применено
          </LegendItem>
          <LegendItem>
            <LegendSwatch $status="applied" />
            Ранее применяли
          </LegendItem>
          <LegendItem>
            <LegendSwatch $status="missed" />
            Пропущено
          </LegendItem>
          <LegendItem>
            <LegendSwatch $status="today" />
            Сегодня
          </LegendItem>
          <LegendItem>
            <LegendSwatch $status="upcoming" />
            Запланировано
          </LegendItem>
        </Legend>
          </>
        )}
      </CalendarSection>

      {/* Подсказка "Как пользоваться календарём" */}
      <OnboardingHint initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}>
        <HintHeader>
          <HintTitle>
            <HintIconWrapper>
              <Lightbulb size={20} />
            </HintIconWrapper>
            <span>Как пользоваться календарём</span>
          </HintTitle>
          <Button
            variant={isHintOpen ? 'secondary' : 'outline'}
            size="small"
            onClick={() => setIsHintOpen((v) => !v)}
            aria-expanded={isHintOpen}
            aria-controls="hint-body"
          >
            {isHintOpen ? 'Свернуть' : 'Подсказка'}
          </Button>
        </HintHeader>

        <AnimatePresence initial={false}>
          {isHintOpen && (
            <HintBody
              id="hint-body"
              key="hint-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <HintText>
                📋 На основе Ваших ответов в анкете мы составили персональный план.
                <br /><br />
                ✨ Следуя ему, Вы сможете эффективно адаптировать кожу к ретинолу и отслеживать динамику.
                <br /><br />
                📅 Отмечайте применение ретинола в соответствующую дату календаря и нажимайте «Да, применено».
              </HintText>
            </HintBody>
          )}
        </AnimatePresence>
      </OnboardingHint>

      {/* Рекомендованные продукты */}
      <ProductShowcase
        products={recommendedProducts ||[]}
        currentStage={currentStage}
      />

      {/* ✅ ИНТЕРЕСНЫЙ ФАКТ — с иконками Lucide */}
      <FactCard initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
        <FactHeader>
          <FactIcon>
            <Sparkles size={28} />
          </FactIcon>
          <FactContent>
            <FactTitleRow>
              <FactTitle>Интересный факт</FactTitle>
              <FactBadge $category={currentFact.category}>
                {FACT_CATEGORY_LABELS[currentFact.category]}
              </FactBadge>
            </FactTitleRow>
            <FactText>{currentFact.text}</FactText>
          </FactContent>
        </FactHeader>
        <FactFooter>
          <RefreshButton
            onClick={handleRefreshFact}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Показать другой факт"
          >
            <span>Показать новый факт</span>
            <RotateCw size={16} />
          </RefreshButton>
        </FactFooter>
      </FactCard>

      <AnimatePresence>
        {selectedDate && (
          <ApplicationModal
            date={selectedDate}
            onClose={() => setSelectedDate(null)}
            onConfirm={handleModalAnswer}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showStageModal && (
          <StageUpgradeModal
            stage={upgradedStage}
            onClose={() => setShowStageModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSyncing && (
          <LoadingOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <LoadingSpinner />
            <LoadingText>{MESSAGES.calendar.syncing}</LoadingText>
          </LoadingOverlay>
        )}
      </AnimatePresence>

      <ApiStatusIndicator />

      {/* ✅ ВАРИАНТ В: Тосты достижений теперь в App.tsx */}

      <ToastPortal>
        <AnimatePresence>
          {toast && (
            <ToastCard
              key={toast.message}
              $tone={toast.tone}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3 }}
            >
              {toast.message}
              {/* ✅ Кнопка закрытия тоста */}
              <ToastCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  setToast(null);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Закрыть"
              >
                <X size={16} strokeWidth={3} />
              </ToastCloseButton>
            </ToastCard>
          )}
        </AnimatePresence>
      </ToastPortal>
    </PageContainer>
  );
};

export default CalendarPage;